import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, User, AtSign, Image as ImageIcon, Lock, Save, Trash2, AlertTriangle, Pause, Smartphone, RotateCcw, MapPinned, ExternalLink, PlusCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionRibbon } from "@/components/AppShell";
import UploadPhotoButton from "@/components/UploadPhotoButton";
import ContinueOnPhoneDialog from "@/components/ContinueOnPhoneDialog";
import PasswordChangeCard from "@/components/profile/PasswordChangeCard";
import RequestAtlasAccessDialog from "@/components/atlas/RequestAtlasAccessDialog";
import { toast } from "sonner";
import { defaultAvatarUrl } from "@/lib/avatar";

const PROFILE_TESTIDS = {
  page: "profile-page",
  nameInput: "profile-name-input",
  usernameInput: "profile-username-input",
  avatarInput: "profile-avatar-input",
  currentPasswordInput: "profile-current-password",
  newPasswordInput: "profile-new-password",
  saveBtn: "profile-save-btn",
  backBtn: "profile-back-btn",
};

function avatarUrl(u) {
  if (!u) return "";
  return u.avatar_url || defaultAvatarUrl(u.email || "");
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [avatarUrlValue, setAvatarUrlValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [closingAccount, setClosingAccount] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setUsername(user.username || "");
      setAvatarUrlValue(user.avatar_url || "");
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      if (name !== (user?.name || "")) payload.name = name;
      if (username !== (user?.username || "")) payload.username = username;
      if (avatarUrlValue !== (user?.avatar_url || "")) payload.avatar_url = avatarUrlValue;
      // Password changes flow through the dedicated
      // <PasswordChangeCard /> below → POST /api/user/change-password.
      // The bundled `/auth/profile` password path is no longer used by
      // this form.
      if (Object.keys(payload).length === 0) {
        toast.info("Nothing changed");
        return;
      }
      await api.put("/auth/profile", payload);
      await refresh();
      toast.success("Profile updated ✨");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const previewAvatar = avatarUrlValue || defaultAvatarUrl(user?.email || "");

  return (
    <div data-testid={PROFILE_TESTIDS.page} className="flex flex-col">
      <SectionRibbon>your profile</SectionRibbon>

      <div className="px-5 pt-3 pb-2">
        <button
          data-testid={PROFILE_TESTIDS.backBtn}
          onClick={() => nav(-1)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-tb-cream-2 border-2 border-tb-cream-3 font-hand text-tb-navy/70 tb-tap text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> back
        </button>
      </div>

      <form onSubmit={submit} className="px-5 pb-8 space-y-5">
        {/* Avatar preview */}
        <div className="flex items-center gap-4">
          <img
            src={previewAvatar}
            alt={name || user?.email}
            className="w-20 h-20 rounded-full border-2 border-tb-gold bg-tb-cream-2 object-cover"
            onError={(e) => {
              e.currentTarget.src = avatarUrl(user);
            }}
          />
          <div className="flex-1 min-w-0">
            <div className="font-serif-it text-tb-navy text-2xl truncate">
              {name || user?.email || "—"}
            </div>
            <div className="font-hand text-tb-navy/60 text-sm truncate">
              @{username || (user?.email || "").split("@")[0]}
            </div>
          </div>
        </div>

        {/* Display name */}
        <label className="block">
          <div className="font-hand text-tb-navy/70 text-sm mb-1 flex items-center gap-1.5">
            <User className="w-3.5 h-3.5" /> Display name
          </div>
          <input
            data-testid={PROFILE_TESTIDS.nameInput}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full px-3 py-2 rounded-lg bg-white border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-tb-navy"
          />
        </label>

        {/* Username */}
        <label className="block">
          <div className="font-hand text-tb-navy/70 text-sm mb-1 flex items-center gap-1.5">
            <AtSign className="w-3.5 h-3.5" /> Username (lounge handle)
          </div>
          <input
            data-testid={PROFILE_TESTIDS.usernameInput}
            value={username}
            onChange={(e) => setUsername(e.target.value.toLowerCase())}
            placeholder="e.g. wanderlust_amy"
            className="w-full px-3 py-2 rounded-lg bg-white border-2 border-tb-cream-3 focus:border-tb-gold outline-none font-ui text-tb-navy"
          />
          <p className="font-hand text-tb-navy/50 text-xs mt-1">letters, numbers, dots, dashes</p>
          <HandleAvailability handle={username} currentUsername={user?.username || ""} />
        </label>

        {/* Profile picture upload */}
        <div>
          <div className="font-hand text-tb-navy/70 text-sm mb-1 flex items-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5" /> Profile picture
          </div>
          <UploadPhotoButton
            testId={PROFILE_TESTIDS.avatarInput}
            value={avatarUrlValue}
            onChange={setAvatarUrlValue}
            max={1}
            label={avatarUrlValue ? "Replace photo" : "Upload photo"}
          />
          <p className="font-hand text-tb-navy/50 text-xs mt-1">
            Photos are compressed in your browser before upload.
          </p>
        </div>

        {/* Password — dedicated PasswordChangeCard (Phase 1 fix).
            The prior inline block used cream inputs, only 2 fields,
            min 6, and piggybacked the shared save button so users
            reported "I don't see a change-password flow". The new
            card is a standalone form with white inputs + its own
            "Update Password" submit. */}
        <PasswordChangeCard />

        <button
          data-testid={PROFILE_TESTIDS.saveBtn}
          type="submit"
          disabled={saving}
          className="w-full py-3 rounded-xl bg-tb-navy text-tb-gold-bright font-hand text-xl border-2 border-tb-gold tb-tap disabled:opacity-60 flex items-center justify-center gap-2"
        >
          <Save className="w-5 h-5" />
          {saving ? "saving…" : "save changes"}
        </button>

        {/* Continue on your phone (mobile handoff) */}
        <div className="mt-6 pt-4 border-t border-dashed border-tb-navy/20">
          <button
            type="button"
            data-testid="profile-continue-on-phone-btn"
            onClick={() => setHandoffOpen(true)}
            className="w-full py-2.5 rounded-xl bg-white border-2 border-tb-navy/30 text-tb-navy font-hand text-base tb-tap hover:bg-tb-navy hover:text-tb-cream flex items-center justify-center gap-2"
          >
            <Smartphone className="w-4 h-4 text-tb-gold-bright" />
            continue on your phone…
          </button>
          <p className="font-hand italic text-[11px] text-tb-navy/55 mt-1.5 text-center">
            Opens a one-time QR that signs you in on the TravelBio mobile app.
          </p>
        </div>

        {/* Danger zone — close or delete account */}
        <div className="mt-8 pt-6 border-t-2 border-dashed border-tb-coral/40">
          <div className="font-hand text-tb-coral text-base flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-4 h-4" /> danger zone
          </div>
          <p className="font-hand text-tb-navy/65 italic text-sm mb-3">
            Need a break or want to wipe your trace? You have two options.
          </p>
          <button
            type="button"
            data-testid="profile-close-account-btn"
            onClick={() => setClosingAccount(true)}
            className="w-full py-2.5 rounded-xl bg-tb-cream border-2 border-tb-coral text-tb-coral font-hand text-base tb-tap hover:bg-tb-coral hover:text-white flex items-center justify-center gap-2"
          >
            <Trash2 className="w-4 h-4" /> close my account…
          </button>
        </div>
      </form>

      {closingAccount && (
        <CloseAccountDialog onClose={() => setClosingAccount(false)} />
      )}
      {handoffOpen && (
        <ContinueOnPhoneDialog onClose={() => setHandoffOpen(false)} />
      )}

      {/* My Submissions — user-submitted destinations. Read-only for
          now: shows status badge + a deep-link back to Explore so the
          user can confirm their card is live in filter results. */}
      <MySubmissionsPanel />
    </div>
  );
}

/**
 * Advisor Atlas — "Request Atlas access" CTA on the traveler profile.
 * Renders ONLY when the user's email lives on the @mytravelbio.com
 * domain AND they don't already have advisor access AND there's no
 * pending or cooldown request outstanding. Hits GET /atlas/access-request/mine
 * on mount to check state; opens the shared dialog on click.
 */
function AtlasAccessCta({ user, refresh }) {
  const [state, setState] = useState(null); // { eligible_to_request, is_advisor, latest_request }
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/atlas/access-request/mine");
        if (!cancelled) setState(data);
      } catch {
        if (!cancelled) setState({ eligible_to_request: false });
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (!user) return null;
  const email = (user.email || "").toLowerCase();
  if (!email.endsWith("@mytravelbio.com")) return null;

  const roles = user.roles || [];
  if (roles.includes("cert_reviewer")) return null;
  if (roles.includes("advisor")) return null;
  if (!state) return null;

  const latest = state.latest_request;
  const pending = latest && latest.status === "pending";

  return (
    <div
      className="mb-4 rounded-xl border border-tb-gold-bright/60 bg-tb-gold/10 p-3"
      data-testid="profile-atlas-access-panel"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div
            className="text-tb-navy text-sm font-semibold"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Advisor Atlas
          </div>
          <p className="mt-0.5 font-hand text-tb-navy/70 text-xs">
            {pending
              ? "Your Atlas access request is pending review. We'll notify you when it's approved."
              : "Get access to the advisor workspace — Knowledge Builder, wallet, and more."}
          </p>
        </div>
        {!pending && (
          <button
            type="button"
            data-testid="profile-request-atlas-btn"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs tb-tap shrink-0"
          >
            <PlusCircle className="w-3.5 h-3.5" />
            Request Atlas Access
          </button>
        )}
        {pending && (
          <span
            className="text-[10px] font-hand uppercase tracking-widest px-2 py-1 rounded-full bg-white border border-tb-navy/20 text-tb-navy shrink-0"
            data-testid="profile-atlas-access-pending"
          >
            Pending
          </span>
        )}
      </div>
      {open && (
        <RequestAtlasAccessDialog
          onClose={() => setOpen(false)}
          onSubmitted={async () => {
            // Re-fetch the /mine record so the panel flips to "Pending"
            // without a manual refresh.
            try {
              const { data } = await api.get("/atlas/access-request/mine");
              setState(data);
            } catch { /* noop */ }
            await refresh?.();
          }}
        />
      )}
    </div>
  );
}

function MySubmissionsPanel() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null); // null = loading, [] = empty
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/submissions");
        if (cancelled) return;
        setRows(Array.isArray(data?.submissions) ? data.submissions : []);
      } catch (e) {
        if (cancelled) return;
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load your submissions");
        setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openInExplore = (s) => {
    // Prefer state deep-link (2-letter abbrev) since Explore's filter
    // matches on that; fall back to a name query if the card has no
    // canonical state (foreign cards).
    const qs = new URLSearchParams();
    if (s.state) qs.set("state", s.state);
    else if (s.name) qs.set("q", s.name);
    nav(`/explore?${qs.toString()}`);
  };

  const statusStyles = {
    live: "bg-tb-sage/15 text-tb-sage border-tb-sage/40",
    pending: "bg-tb-gold/15 text-tb-navy border-tb-gold/60",
    rejected: "bg-tb-coral/15 text-tb-coral border-tb-coral/50",
  };
  const statusLabels = { live: "live", pending: "pending", rejected: "rejected" };

  return (
    <div
      data-testid="profile-submissions-panel"
      className="tb-paper-soft rounded-2xl p-5 mt-4 space-y-3"
    >
      <div className="flex items-center gap-2">
        <MapPinned className="w-5 h-5 text-tb-gold-bright" />
        <h2 className="font-serif-it text-tb-navy text-2xl leading-tight">My Submissions</h2>
      </div>
      <p className="font-hand text-tb-navy/60 italic text-sm">
        Destinations you've added to the TravelBio catalog.
      </p>

      {rows === null ? (
        <p className="font-hand text-tb-navy/50 italic text-sm">{"Loading\u2026"}</p>
      ) : error ? (
        <p className="font-hand text-tb-coral text-sm">{error}</p>
      ) : rows.length === 0 ? (
        <div className="text-center py-6">
          <p className="font-hand text-tb-navy/60 italic mb-3">
            You haven't submitted any destinations yet. Tap the + on Explore to share one.
          </p>
          <button
            type="button"
            data-testid="profile-submissions-empty-cta"
            onClick={() => nav("/explore")}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm tb-tap"
          >
            <PlusCircle className="w-4 h-4" /> Add a destination
          </button>
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.map((s) => (
            <li
              key={s.id}
              data-testid={`profile-submission-row-${s.id}`}
              className="flex items-center gap-3 rounded-xl border-2 border-tb-cream-3 bg-tb-cream-2/50 p-2.5"
            >
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-tb-cream-3 shrink-0 flex items-center justify-center">
                {s.thumbnail ? (
                  <img src={s.thumbnail} alt="" aria-hidden="true" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-5 h-5 text-tb-navy/30" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-tb-navy text-base truncate">{s.name}</div>
                <div className="font-hand text-tb-navy/60 text-xs truncate">
                  {[s.city, s.state].filter(Boolean).join(", ")}
                  {s.country && s.country !== "USA" ? ` \u00B7 ${s.country}` : ""}
                </div>
              </div>
              <span
                data-testid={`profile-submission-status-${s.id}`}
                className={
                  "px-2 py-0.5 rounded-full border font-hand text-[10px] uppercase tracking-wider shrink-0 " +
                  (statusStyles[s.status] || statusStyles.live)
                }
              >
                {statusLabels[s.status] || s.status}
              </span>
              <button
                type="button"
                data-testid={`profile-submission-open-${s.id}`}
                onClick={() => openInExplore(s)}
                title="Show on Explore"
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs tb-tap shrink-0 hover:brightness-110 transition"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Show on Explore
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CloseAccountDialog({ onClose }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("pick");   // pick | confirm-save | confirm-delete
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (mode) => {
    setBusy(true);
    try {
      if (mode === "deactivate") {
        await api.post("/auth/account/deactivate", { password });
        toast.success("Account paused — sign in any time to come back");
      } else {
        await api.delete("/auth/account", { data: { password } });
        toast.success("Account deleted — safe travels ✦");
      }
      await logout?.();
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't close account");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-tb-navy-deep/70 flex items-end md:items-center justify-center p-3"
      role="dialog"
      aria-modal="true"
      data-testid="close-account-dialog"
    >
      <div className="bg-tb-cream rounded-2xl border-2 border-tb-coral max-w-md w-full">
        <div className="bg-tb-coral text-white px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <span className="font-hand text-xl flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> close account
          </span>
          <button type="button" onClick={onClose} className="font-hand tb-tap">close</button>
        </div>
        <div className="p-4 space-y-3">
          {step === "pick" && (
            <>
              <p className="font-serif-it text-tb-navy text-lg leading-snug">
                What would you like to do with your travel journal?
              </p>
              <button
                type="button"
                data-testid="close-account-pause-btn"
                onClick={() => setStep("confirm-save")}
                className="w-full text-left p-3 rounded-xl border-2 border-tb-blue bg-tb-blue-soft hover:bg-tb-blue hover:text-white tb-tap group"
              >
                <div className="font-hand text-tb-navy text-lg group-hover:text-white flex items-center gap-2">
                  <Pause className="w-4 h-4" /> save my info for later
                </div>
                <p className="font-ui text-xs text-tb-navy/70 group-hover:text-white/90 mt-1">
                  pause the account · sign back in any time to pick up where you left off
                </p>
              </button>
              <button
                type="button"
                data-testid="close-account-delete-btn"
                onClick={() => setStep("confirm-delete")}
                className="w-full text-left p-3 rounded-xl border-2 border-tb-coral bg-tb-coral-soft hover:bg-tb-coral hover:text-white tb-tap group"
              >
                <div className="font-hand text-tb-coral text-lg group-hover:text-white flex items-center gap-2">
                  <Trash2 className="w-4 h-4" /> delete everything for good
                </div>
                <p className="font-ui text-xs text-tb-coral/80 group-hover:text-white/90 mt-1">
                  permanently erase your chapters, dreams, posts and stamps · this can&apos;t be undone
                </p>
              </button>
            </>
          )}
          {(step === "confirm-save" || step === "confirm-delete") && (
            <>
              <p className="font-serif-it text-tb-navy text-lg leading-snug">
                {step === "confirm-save"
                  ? "Pause this account — your data sticks around."
                  : "Permanently delete everything? This can't be undone."}
              </p>
              <label className="block">
                <span className="font-hand text-tb-navy/65 text-sm">Confirm with your password</span>
                <input
                  type="password"
                  data-testid="close-account-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="current password"
                  className="w-full px-3 py-2 rounded-lg bg-tb-cream border-2 border-tb-cream-3 focus:border-tb-coral outline-none font-ui text-sm"
                />
                <p className="font-hand text-tb-navy/50 text-xs italic mt-1">
                  leave blank if you signed in with Google
                </p>
              </label>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setStep("pick")}
                  className="flex-1 py-2.5 rounded-xl bg-tb-cream-2 border-2 border-tb-cream-3 font-hand text-tb-navy/70 tb-tap"
                >
                  back
                </button>
                <button
                  type="button"
                  disabled={busy}
                  data-testid="close-account-confirm-btn"
                  onClick={() => submit(step === "confirm-save" ? "deactivate" : "delete")}
                  className={
                    "flex-1 py-2.5 rounded-xl font-hand text-lg tb-tap border-2 disabled:opacity-60 " +
                    (step === "confirm-save"
                      ? "bg-tb-blue text-white border-tb-blue"
                      : "bg-tb-coral text-white border-tb-coral")
                  }
                >
                  {busy ? "working…" : step === "confirm-save" ? "pause account" : "delete forever"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Item 2 — Inline live handle-availability check.
// Debounces at 400ms and hits GET /auth/handle-available?handle=X.
// Returns nothing when the value equals the user's current username
// (they can re-save it) or is too short — otherwise renders a
// tiny "taken / available" line under the input.
function HandleAvailability({ handle, currentUsername }) {
  const [state, setState] = React.useState(null); // null | "checking" | "available" | "taken" | "invalid"
  const trimmed = (handle || "").trim().toLowerCase();
  React.useEffect(() => {
    if (!trimmed) { setState(null); return; }
    if (trimmed === (currentUsername || "").toLowerCase()) { setState(null); return; }
    if (trimmed.length < 2) { setState("invalid"); return; }
    setState("checking");
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await import("@/lib/api").then((m) =>
          m.api.get("/auth/handle-available", { params: { handle: trimmed }, signal: ctrl.signal })
        );
        if (data.available) setState("available");
        else if (data.reason === "invalid_chars" || data.reason === "too_short") setState("invalid");
        else setState("taken");
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") setState(null);
      }
    }, 400);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [trimmed, currentUsername]);

  if (!state) return null;
  const label = {
    checking:  "checking availability…",
    available: "✓ available",
    taken:     "handle taken — try another.",
    invalid:   "handle too short or contains invalid characters.",
  }[state];
  const color = state === "available" ? "text-tb-gold"
              : state === "checking"  ? "text-tb-navy/50"
              :                         "text-tb-coral";
  return (
    <p
      data-testid="profile-handle-availability"
      data-state={state}
      className={`font-hand ${color} text-xs mt-0.5`}
    >
      {label}
    </p>
  );
}
